import React, { useState, useEffect, useContext, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AuthContext from "../AuthContext";
import instaLogo from "../images/instalogo.png";

// ── localStorage helpers ──────────────────────────────────────────────────────

// Queue data is kept separate per mode ("customer" vs "task") so switching
// modes shows a completely independent list instead of sharing one array.
const loadQueue = (userId, mode) => {
  try {
    return JSON.parse(localStorage.getItem(`queue_${userId}_${mode}`)) || [];
  } catch {
    return [];
  }
};

const saveQueue = (userId, mode, queue) => {
  localStorage.setItem(`queue_${userId}_${mode}`, JSON.stringify(queue));
};

const nextQueueNumber = (userId, mode) => {
  const key = `queue_counter_${userId}_${mode}`;
  const n = parseInt(localStorage.getItem(key) || "0", 10) + 1;
  localStorage.setItem(key, String(n));
  return n;
};

// One-time migration: earlier versions stored the queue under a single key
// shared between customer/task modes. Move that legacy data into "customer"
// (the original default mode) so existing data isn't lost.
const migrateLegacyQueueKeys = (userId) => {
  const legacyQueue = localStorage.getItem(`queue_${userId}`);
  if (legacyQueue && !localStorage.getItem(`queue_${userId}_customer`)) {
    localStorage.setItem(`queue_${userId}_customer`, legacyQueue);
    localStorage.removeItem(`queue_${userId}`);
  }
  const legacyServing = localStorage.getItem(`serving_nums_${userId}`);
  if (legacyServing && !localStorage.getItem(`serving_nums_${userId}_customer`)) {
    localStorage.setItem(`serving_nums_${userId}_customer`, legacyServing);
    localStorage.removeItem(`serving_nums_${userId}`);
  }
  const legacyCounter = localStorage.getItem(`queue_counter_${userId}`);
  if (legacyCounter && !localStorage.getItem(`queue_counter_${userId}_customer`)) {
    localStorage.setItem(`queue_counter_${userId}_customer`, legacyCounter);
    localStorage.removeItem(`queue_counter_${userId}`);
  }
};

// Formats a past timestamp (ms) as a short "time ago" string, e.g. "just now",
// "5 min ago", "1 hr ago". Returns null if no timestamp is available.
const formatTimeAgo = (timestamp) => {
  if (!timestamp) return null;
  const diffMs = Date.now() - timestamp;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin === 1) return "1 min ago";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  return diffHr === 1 ? "1 hr ago" : `${diffHr} hrs ago`;
};

// ── Simple beep/tone player (no external audio files needed) ──────────────────
let sharedAudioCtx = null;
const getAudioCtx = () => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  if (!sharedAudioCtx) sharedAudioCtx = new AudioCtx();
  return sharedAudioCtx;
};

const playTone = (freq, duration, type = "sine") => {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = type;
    oscillator.frequency.value = freq;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration / 1000);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + duration / 1000);
  } catch {
    // Audio not supported/blocked — fail silently
  }
};

// Pleasant two-note "ding" played when a new customer checks in
const playCheckInSound = () => {
  playTone(880, 150);
  setTimeout(() => playTone(1175, 150), 120);
};

// Longer, descending alert tone played after confirming removal of a waiting customer/task
const playDeleteSound = () => {
  playTone(320, 220, "square");
  setTimeout(() => playTone(200, 260, "square"), 180);
};

// ── Served-history helpers (for the Statistics panel) ─────────────────────────

const toLocalDateStr = (d = new Date()) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const logCheckIn = (userId) => {
  const key = `checkin_history_${userId}`;
  const history = JSON.parse(localStorage.getItem(key) || "[]");
  history.push(toLocalDateStr());
  // Cap growth — only the last 1000 entries are kept
  localStorage.setItem(key, JSON.stringify(history.slice(-1000)));
};

const getLast7DaysStats = (userId) => {
  const history = JSON.parse(localStorage.getItem(`checkin_history_${userId}`) || "[]");
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = toLocalDateStr(d);
    const label = i === 0 ? "Today" : d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    const count = history.filter((h) => h === dateStr).length;
    days.push({ dateStr, label, count });
  }
  return days;
};

// ── Deleted-customer tracking (for the Statistics panel) ──────────────────────
const logDeleted = (userId) => {
  const key = `deleted_history_${userId}`;
  const history = JSON.parse(localStorage.getItem(key) || "[]");
  history.push(toLocalDateStr());
  localStorage.setItem(key, JSON.stringify(history.slice(-1000)));
};

const getLast7DaysDeletedTotal = (userId) => {
  const history = JSON.parse(localStorage.getItem(`deleted_history_${userId}`) || "[]");
  let total = 0;
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = toLocalDateStr(d);
    total += history.filter((h) => h === dateStr).length;
  }
  return total;
};

// ── Wait-time tracking (checked-in → started being served) ────────────────
// Only the most recently served customer's actual wait time is kept — this is
// meant to reflect current conditions, not a smoothed-out historical average.
const recordWaitTime = (userId, waitMs) => {
  if (!Number.isFinite(waitMs) || waitMs < 0) return;
  localStorage.setItem(`last_wait_time_${userId}`, String(waitMs));
};

const getLastWaitMinutes = (userId) => {
  const raw = localStorage.getItem(`last_wait_time_${userId}`);
  if (raw === null) return null;
  const waitMs = Number(raw);
  if (!Number.isFinite(waitMs)) return null;
  return Math.round(waitMs / 60000);
};

// Client-side JWT expiry check — no backend call needed
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

// ── Daily visitor-count sync (best-effort, silent) ─────────────────────────
// Report every finalized day's (i.e. not today, since it isn't over yet)
// checked-in customer count to the backend so it's saved on the business's
// profile. This is purely a nice-to-have background sync — any failure
// (server down, network issue, etc.) is swallowed completely so the person
// running the queue never sees an error about it; unsent days are simply
// retried the next time this runs.
//
// Note: this intentionally backfills *all* pending finalized days (not just
// "yesterday"), and is called both on page load and on a recurring interval
// (see the effect below), because this page is often left open for many
// days straight on a kiosk-style display — a single "check on mount" would
// otherwise only ever sync the first day and never run again.
const syncPendingVisitorCounts = async (userId) => {
  const todayStr = toLocalDateStr();
  const history = JSON.parse(localStorage.getItem(`checkin_history_${userId}`) || "[]");
  const finalizedDates = [...new Set(history.filter((h) => h < todayStr))];
  if (finalizedDates.length === 0) return;

  const syncedKey = `visitor_synced_dates_${userId}`;
  let syncedDates = JSON.parse(localStorage.getItem(syncedKey) || "[]");
  const pending = finalizedDates.filter((d) => !syncedDates.includes(d));

  for (const dateStr of pending) {
    try {
      const count = history.filter((h) => h === dateStr).length;
      await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/api/queue/visitors`,
        { date: dateStr, count },
        { headers: { Authorization: localStorage.getItem("token") } }
      );
      // Only remember success — on failure we stop and retry this (and any
      // later) date next time, so nothing is silently skipped.
      syncedDates = [...syncedDates, dateStr].slice(-90);
      localStorage.setItem(syncedKey, JSON.stringify(syncedDates));
    } catch {
      break;
    }
  }
};

// ─────────────────────────────────────────────────────────────────────────────

const QueueManager = () => {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [queue, setQueue] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [newNumber, setNewNumber] = useState(null);
  const [notes, setNotes] = useState("");
  const [notesLabel, setNotesLabel] = useState("Notes");
  const [editingLabel, setEditingLabel] = useState(false);
  const [activeTab, setActiveTab] = useState("checkin");
  const [servingNums, setServingNums] = useState([]);
  const [mode, setMode] = useState("customer"); // "customer" | "task"
  const [priority, setPriority] = useState("medium");
  const [showStats, setShowStats] = useState(false);
  const [showServing, setShowServing] = useState(false);
  const [confirmRemoveNumber, setConfirmRemoveNumber] = useState(null);
  const [aheadCount, setAheadCount] = useState(0);

  const upNextScrollRef = useRef(null);
  const upNextScrollTimeout = useRef(null);

  // Ticks every 30s purely to force a re-render so the "X min ago" labels
  // in the Up next list stay fresh without needing to reload the page.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setNowTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll the "Up next" list back to the top 5s after the user stops scrolling it
  const handleUpNextScroll = () => {
    if (upNextScrollTimeout.current) clearTimeout(upNextScrollTimeout.current);
    upNextScrollTimeout.current = setTimeout(() => {
      upNextScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, 5000);
  };

  useEffect(() => () => clearTimeout(upNextScrollTimeout.current), []);

  // ── Auth guard: check token expiry client-side ────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("token");
    const stored = localStorage.getItem("user");
    if (!token || !stored || isTokenExpired(token)) {
      logout();
      navigate("/login");
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      if (!parsed?.id) {
        navigate("/login");
        return;
      }
      setUser(parsed);
      migrateLegacyQueueKeys(parsed.id);
      const savedMode = localStorage.getItem(`queue_mode_${parsed.id}`) || "customer";
      setMode(savedMode);
      setQueue(loadQueue(parsed.id, savedMode));
      const savedLabel = localStorage.getItem(`notes_label_${parsed.id}`);
      if (savedLabel) setNotesLabel(savedLabel);
      const savedServing = localStorage.getItem(`serving_nums_${parsed.id}_${savedMode}`);
      if (savedServing) setServingNums(JSON.parse(savedServing));
      syncPendingVisitorCounts(parsed.id);
    } catch {
      navigate("/login");
    }
  }, [navigate, logout]);

  // Long-lived kiosk-style sessions can stay on this page for many days
  // without a reload, so the mount-time sync above would only ever run
  // once. Re-check periodically as well so a new finalized day's count
  // (and any that failed earlier due to a network/server hiccup) gets sent
  // without requiring the page to be refreshed.
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => syncPendingVisitorCounts(user.id), 60 * 60 * 1000); // hourly
    return () => clearInterval(interval);
  }, [user]);

  // ── Sync queue + serving status across tabs (same device, multiple browser tabs) ──
  useEffect(() => {
    if (!user) return;
    const onStorage = (e) => {
      if (e.key === `queue_mode_${user.id}`) {
        const newMode = e.newValue || "customer";
        setMode(newMode);
        setQueue(loadQueue(user.id, newMode));
        const sn = localStorage.getItem(`serving_nums_${user.id}_${newMode}`);
        setServingNums(sn ? JSON.parse(sn) : []);
        return;
      }
      if (e.key === `queue_${user.id}_${mode}`) {
        setQueue(e.newValue ? JSON.parse(e.newValue) : []);
      }
      if (e.key === `serving_nums_${user.id}_${mode}`) {
        setServingNums(e.newValue ? JSON.parse(e.newValue) : []);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [user, mode]);

  // ── Auto-reset check-in panel after 10 s ─────────────────────────────────
  useEffect(() => {
    if (!submitted) return;
    const t = setTimeout(resetForm, 10000);
    return () => clearTimeout(t);
  }, [submitted]);

  // ── Auto-switch back to Check In after 15 s of inactivity on Queue tab ───
  useEffect(() => {
    // Only auto-switch in customer mode — task mode users manage the board intentionally
    if (activeTab !== "queue" || mode !== "customer") return;
    let timer = setTimeout(() => setActiveTab("checkin"), 15000);
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setActiveTab("checkin"), 15000);
    };
    window.addEventListener("mousemove", reset);
    window.addEventListener("touchstart", reset);
    window.addEventListener("click", reset);
    window.addEventListener("keydown", reset);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", reset);
      window.removeEventListener("touchstart", reset);
      window.removeEventListener("click", reset);
      window.removeEventListener("keydown", reset);
    };
  }, [activeTab, mode]);

  // ── Queue actions ─────────────────────────────────────────────────────────

  const handleJoin = (e) => {
    e.preventDefault();
    const number = nextQueueNumber(user.id, mode);
    const updated = [...queue, { number, name, phone: phone || null, notes: notes || null, priority: mode === "task" ? priority : null, checkedInAt: Date.now() }];
    setQueue(updated);
    saveQueue(user.id, mode, updated);
    // The ticket number shown to the customer is always the real, ever-incrementing
    // queue number (never reused) — how many people are still ahead of them is
    // tracked separately below, purely for the "X customers ahead of you" message.
    const ahead = updated.filter((c) => !servingNums.includes(c.number) && c.number !== number).length;
    setAheadCount(ahead);
    setNewNumber(number);
    setSubmitted(true);
    playCheckInSound();
    if (mode === "customer") {
      logCheckIn(user.id);
    }
  };

  const resetForm = () => {
    setName("");
    setPhone("");
    setNotes("");
    setPriority("medium");
    setNewNumber(null);
    setSubmitted(false);
  };

  const toggleMode = () => {
    const newMode = mode === "customer" ? "task" : "customer";
    setMode(newMode);
    if (user) {
      localStorage.setItem(`queue_mode_${user.id}`, newMode);
      setQueue(loadQueue(user.id, newMode));
      const sn = localStorage.getItem(`serving_nums_${user.id}_${newMode}`);
      setServingNums(sn ? JSON.parse(sn) : []);
    }
  };

  const serveNow = (number) => {
    if (!user) return;
    if (mode === "customer") {
      const cust = queue.find((c) => c.number === number);
      if (cust?.checkedInAt) {
        recordWaitTime(user.id, Date.now() - cust.checkedInAt);
      }
    }
    const updated = [...servingNums, number];
    setServingNums(updated);
    localStorage.setItem(`serving_nums_${user.id}_${mode}`, JSON.stringify(updated));
  };

  const markServedByNumber = (number) => {
    if (!user) return;
    const updatedQueue = queue.filter((c) => c.number !== number);
    setQueue(updatedQueue);
    saveQueue(user.id, mode, updatedQueue);
    const updatedServing = servingNums.filter((n) => n !== number);
    setServingNums(updatedServing);
    localStorage.setItem(`serving_nums_${user.id}_${mode}`, JSON.stringify(updatedServing));
    if (updatedQueue.length === 0) {
      localStorage.removeItem(`queue_counter_${user.id}_${mode}`);
    }
  };

  // Marks everyone currently being served as served in one go, while still
  // allowing individual customers to be marked served one at a time (unchanged).
  const markAllServed = () => {
    if (!user || serving.length === 0) return;
    const servedNumbers = serving.map((c) => c.number);
    const updatedQueue = queue.filter((c) => !servedNumbers.includes(c.number));
    setQueue(updatedQueue);
    saveQueue(user.id, mode, updatedQueue);
    setServingNums([]);
    localStorage.setItem(`serving_nums_${user.id}_${mode}`, JSON.stringify([]));
    if (updatedQueue.length === 0) {
      localStorage.removeItem(`queue_counter_${user.id}_${mode}`);
    }
  };

  const requestRemoveCustomer = (number) => {
    setConfirmRemoveNumber(number);
  };

  const confirmRemoveCustomer = () => {
    if (!user || confirmRemoveNumber === null) return;
    const number = confirmRemoveNumber;
    const updatedQueue = queue.filter((c) => c.number !== number);
    setQueue(updatedQueue);
    saveQueue(user.id, mode, updatedQueue);
    const updatedServing = servingNums.filter((n) => n !== number);
    setServingNums(updatedServing);
    localStorage.setItem(`serving_nums_${user.id}_${mode}`, JSON.stringify(updatedServing));
    if (mode === "customer") {
      logDeleted(user.id);
    }
    playDeleteSound();
    setConfirmRemoveNumber(null);
  };

  const clearAll = () => {
    if (!user) return;
    if (!window.confirm("Clear the entire queue and reset the counter? This cannot be undone.")) return;
    setQueue([]);
    setServingNums([]);
    saveQueue(user.id, mode, []);
    localStorage.removeItem(`queue_counter_${user.id}_${mode}`);
    localStorage.setItem(`serving_nums_${user.id}_${mode}`, JSON.stringify([]));
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const current = queue[0];
  const serving = queue.filter((c) => servingNums.includes(c.number));
  const waiting = queue.filter((c) => !servingNums.includes(c.number));
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  const sortedWaiting = mode === "task"
    ? [...waiting].sort((a, b) => (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1))
    : waiting;
  // Displayed queue number — the real, ever-incrementing ticket number assigned
  // at check-in (never reused), so it matches what the customer was shown.
  const displayNumber = (cust) => cust.number;

  // Stats-modal derived data — only computed while the modal is open
  const statsData = showStats && user ? getLast7DaysStats(user.id) : [];
  const statsTotal = statsData.reduce((sum, d) => sum + d.count, 0);
  const statsMax = Math.max(1, ...statsData.map((d) => d.count));
  const statsAvg = statsData.length ? (statsTotal / statsData.length).toFixed(1) : "0.0";
  const statsBest = statsData.length
    ? statsData.reduce((a, b) => (b.count > a.count ? b : a), statsData[0])
    : null;
  const statsDeletedTotal = showStats && user ? getLast7DaysDeletedTotal(user.id) : 0;
  const lastWaitMinutes = user ? getLastWaitMinutes(user.id) : null;

  return (
    <div className="h-screen-safe flex flex-col overflow-hidden bg-gray-100">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="relative bg-white shadow-sm px-4 sm:px-6 py-3 flex justify-between items-center flex-shrink-0 z-10">
        <div className="min-w-0 max-w-[35%]">
          <h1 className="text-base sm:text-xl font-bold text-gray-800 truncate">
            {user?.businessName
              ? user.businessName.charAt(0).toUpperCase() + user.businessName.slice(1)
              : "Queue Manager"}
          </h1>
          <button
            onClick={toggleMode}
            className={`hidden sm:inline-flex mt-0.5 text-xs px-2 py-0.5 rounded-full font-semibold border transition items-center gap-1 ${
              mode === "customer"
                ? "bg-blue-50 text-blue-600 border-blue-300 hover:bg-blue-100"
                : "bg-purple-50 text-purple-600 border-purple-300 hover:bg-purple-100"
            }`}
          >
            {mode === "customer" ? "� Switch to Tasks Mode" : "👥 Switch to Queue Mode"}
          </button>
        </div>

        {/* Branding — always visible, centred */}
        <a
          href="https://customerqueue.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center absolute left-1/2 -translate-x-1/2 max-w-[30%] sm:max-w-none group pointer-events-auto"
        >
          <span className="text-sm sm:text-2xl font-extrabold tracking-tight bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 bg-clip-text text-transparent group-hover:from-emerald-600 group-hover:via-teal-600 group-hover:to-cyan-600 transition-all duration-300 leading-tight drop-shadow-sm">
            CustomerQueue.com
          </span>
          <span className="hidden sm:block text-[10px] font-semibold tracking-[0.2em] uppercase mt-0.5 bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent group-hover:from-teal-500 group-hover:to-cyan-500 transition-colors duration-300">
            ✦ Queue smarter ✦
          </span>
        </a>

        <div className="flex-shrink-0 flex items-center gap-2">
          <button
            onClick={() => window.location.reload()}
            title="Refresh the app"
            className="bg-gray-50 text-gray-600 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition text-sm font-medium"
          >
            ⟳ Refresh
          </button>
          <button
            onClick={handleLogout}
            className="bg-red-50 text-red-600 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-100 transition text-sm font-medium"
          >
            Logout
          </button>
        </div>
      </header>

      {/* ── Mobile tab switcher ───────────────────────────────────────────── */}      {/* Mobile-only mode toggle */}
      <div className="sm:hidden flex justify-center bg-white border-b border-gray-100 py-1.5 flex-shrink-0">
        <button
          onClick={toggleMode}
          className={`text-xs px-4 py-1 rounded-full font-semibold border transition ${
            mode === "customer"
              ? "bg-blue-50 text-blue-600 border-blue-300 hover:bg-blue-100"
              : "bg-purple-50 text-purple-600 border-purple-300 hover:bg-purple-100"
          }`}
        >
          {mode === "customer" ? "📋 Switch to Tasks Mode" : "👥 Switch to Queue Mode"}
        </button>
      </div>      <div className="flex border-b border-gray-200 bg-white flex-shrink-0">
        <button
          onClick={() => setActiveTab("checkin")}
          className={`flex-1 py-2.5 text-sm font-semibold transition ${
            activeTab === "checkin"
              ? "text-green-600 border-b-2 border-green-500"
              : "text-gray-400 hover:text-gray-600"
          }`}
        >
          {mode === "customer" ? "👋 Check In" : "📋 Add Task"}
        </button>
        <button
          onClick={() => setActiveTab("queue")}
          className={`flex-1 py-2.5 text-sm font-semibold transition ${
            activeTab === "queue"
              ? "text-green-600 border-b-2 border-green-500"
              : "text-gray-400 hover:text-gray-600"
          }`}
        >
          {mode === "customer" ? "📋 Queue" : "📊 Tasks"}{queue.length > 0 ? ` (${queue.length})` : ""}
        </button>
      </div>

      {/* ── Body: two columns on lg+, tabs on mobile ─────────────────────── */}
      <div className="flex-1 flex overflow-hidden">

        {/* ── LEFT PANEL: Customer Check-In ───────────────────────────────── */}
        <div
          className={`${activeTab === "checkin" ? "flex w-full" : "hidden"} bg-white flex-col overflow-hidden`}
        >
        <div
          ref={upNextScrollRef}
          onScroll={handleUpNextScroll}
          className="flex-1 flex flex-col items-center justify-start px-6 sm:px-10 py-6 sm:py-10 overflow-y-auto"
        >
          <div className="text-center mb-4 sm:mb-8">
            {mode === "customer" && !submitted && (
              <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
                <p className="text-sm font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 inline-block px-3 py-1 rounded-full">
                  {waiting.length} {waiting.length === 1 ? "customer" : "customers"} currently waiting
                </p>
                {lastWaitMinutes !== null && (
                  <p className="text-sm font-semibold text-teal-600 bg-teal-50 border border-teal-100 inline-block px-3 py-1 rounded-full">
                    ⏱ ~{lastWaitMinutes} min last wait
                  </p>
                )}
              </div>
            )}
            <div className="text-4xl sm:text-5xl mb-2 sm:mb-3">{mode === "customer" ? "👋" : "📋"}</div>
            <h2 className="text-xl sm:text-3xl font-bold text-gray-800 mb-1">
              {mode === "customer" ? "Please check in here" : "Add a New Task"}
            </h2>
            <p className="text-sm text-gray-400">{mode === "customer" ? "Enter your name and join the queue instantly" : "Enter task details and set its priority"}</p>
          </div>

          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.form
                key="form"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                onSubmit={handleJoin}
                className="w-full max-w-sm"
              >
                <label className="block mb-4">
                  <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    {mode === "customer" ? "Name" : "Task Title"} *
                  </span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoComplete="off"
                    className="w-full mt-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                    placeholder={mode === "customer" ? "Enter your name" : "Enter task title"}
                  />
                </label>

                {mode === "task" && (
                  <div className="block mb-4">
                    <span className="text-sm font-medium text-gray-500 uppercase tracking-wide block mb-2">Priority</span>
                    <div className="flex gap-2">
                      {[
                        { value: "high", label: "🔴 High", activeClass: "bg-red-100 border-red-500 text-red-700" },
                        { value: "medium", label: "🟡 Medium", activeClass: "bg-yellow-100 border-yellow-500 text-yellow-700" },
                        { value: "low", label: "🔵 Low", activeClass: "bg-blue-100 border-blue-400 text-blue-700" },
                      ].map(({ value, label, activeClass }) => (
                        <button
                          type="button"
                          key={value}
                          onClick={() => setPriority(value)}
                          className={`flex-1 py-2 rounded-lg font-semibold text-sm border-2 transition ${
                            priority === value ? activeClass : "border-gray-200 text-gray-400 hover:border-gray-300 bg-white"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <label className="hidden mb-6">
                  <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Phone (optional)
                  </span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full mt-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                    placeholder="Phone number"
                  />
                </label>

                <label className="block mb-6">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-gray-500 uppercase tracking-wide mb-1">
                    {editingLabel ? (
                      <input
                        type="text"
                        value={notesLabel}
                        onChange={(e) => setNotesLabel(e.target.value)}
                        onBlur={() => {
                          setEditingLabel(false);
                          if (user) localStorage.setItem(`notes_label_${user.id}`, notesLabel);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setEditingLabel(false);
                            if (user) localStorage.setItem(`notes_label_${user.id}`, notesLabel);
                          }
                        }}
                        autoFocus
                        className="border-b border-gray-400 bg-transparent text-sm font-medium text-gray-500 uppercase tracking-wide focus:outline-none w-32"
                      />
                    ) : (
                      <>
                        {notesLabel}
                        <button
                          type="button"
                          onClick={() => setEditingLabel(true)}
                          title="Edit label"
                          className="text-gray-300 hover:text-gray-500 transition text-base leading-none"
                        >
                          ✎
                        </button>
                      </>
                    )}
                    <span className="normal-case font-normal tracking-normal text-gray-400">(optional)</span>
                  </span>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full mt-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                    placeholder="Type here..."
                  />
                </label>

                <button
                  type="submit"
                  className="w-full bg-green-600 text-white text-xl font-bold py-4 rounded-xl hover:bg-green-700 active:scale-95 transition shadow-md"
                >
                  {mode === "customer" ? "Join Queue →" : "Add Task →"}
                </button>

                {mode === "customer" && waiting.length > 0 && (
                  <div className="mt-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="h-px flex-1 bg-gray-200" />
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                        Up next
                      </p>
                      <span className="h-px flex-1 bg-gray-200" />
                    </div>
                    <div className="space-y-1.5 pr-1">
                      {waiting.map((c, idx) => (
                        <div
                          key={c.number}
                          className={`flex items-center gap-3 rounded-lg px-3 py-2 border transition ${
                            idx === 0
                              ? "bg-indigo-50 border-indigo-200"
                              : "bg-gray-50 border-gray-100"
                          }`}
                        >
                          <span
                            className={`flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-full text-xs font-bold flex-shrink-0 ${
                              idx === 0 ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-500"
                            }`}
                          >
                            #{c.number}
                          </span>
                          <span className="truncate text-sm font-medium text-gray-600 flex-1">{c.name}</span>
                          {formatTimeAgo(c.checkedInAt) && (
                            <span className="text-xs text-gray-400 flex-shrink-0">{formatTimeAgo(c.checkedInAt)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.form>
            ) : (
              <motion.div
                key="ticket"
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                className="text-center w-full max-w-sm"
              >
                <p className="text-gray-500 text-lg mb-1">{mode === "customer" ? "Your queue number is" : "Task added!"}</p>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20 }}
                  className="text-7xl sm:text-9xl font-extrabold text-green-600 leading-none mb-3"
                >
                  #{newNumber}
                </motion.div>
                {mode === "customer" && (
                  <p className="text-sm font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 inline-block px-3 py-1 rounded-full mb-3">
                    {aheadCount <= 0
                      ? "You're up next!"
                      : `${aheadCount} customer${aheadCount === 1 ? "" : "s"} ahead of you`}
                  </p>
                )}
                <p className="text-xl sm:text-2xl font-semibold text-gray-800 mb-2">
                  {mode === "customer" ? `Thanks, ${name}!` : name}
                </p>
                {mode === "task" && (
                  <p className={`text-sm font-semibold px-3 py-1 rounded-full mb-2 inline-block ${priority === "high" ? "bg-red-100 text-red-600" : priority === "medium" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-600"}`}>
                    {priority === "high" ? "🔴 High Priority" : priority === "medium" ? "🟡 Medium Priority" : "🔵 Low Priority"}
                  </p>
                )}
                <p className="text-gray-500 text-sm mb-1">{mode === "customer" ? "Please wait to be called" : "Task is in the queue"}</p>
                <p className="text-gray-300 text-xs mb-6">Screen resets in 10 seconds</p>
                <button
                  onClick={resetForm}
                  className="bg-blue-600 text-white font-semibold py-2 px-8 rounded-lg hover:bg-blue-700 transition"
                >
                  Done
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0">
            <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-gray-400">
              <span>
                Powered by{" "}
                <a
                  href="https://customerqueue.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 font-semibold hover:underline"
                >
                  customerqueue.com
                </a>
              </span>
              <span>·</span>
              <a href="tel:+61466127889" className="text-blue-500 font-semibold hover:underline">
                📞 +61 466 127 889
              </a>
              <span>·</span>
              <a
                href="https://instagram.com/customer.queue"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-500 font-semibold hover:underline"
              >
                <img src={instaLogo} alt="Instagram" className="w-4 h-4" />
                @customer.queue
              </a>
            </p>
          </div>
        </div>

        {/* ── RIGHT PANEL: Queue Display + Admin Controls ────────────────────── */}
        <div
          className={`${activeTab === "queue" ? "flex w-full" : "hidden"} bg-gray-50 flex-col overflow-hidden`}
        >

          {/* Now Serving — compact summary, opens full details in a modal; "Mark all served" clears everyone currently being served in one go */}
          <div className="flex-shrink-0 bg-white px-4 sm:px-6 py-3 shadow-sm border-b border-gray-200 flex items-center justify-between gap-3">
            <button
              onClick={() => setShowServing(true)}
              className="flex items-center gap-3 min-w-0 flex-1 text-left hover:opacity-75 transition"
            >
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest flex-shrink-0">
                {mode === "customer" ? "Now Serving" : "In Progress"}
              </span>
              <span className="flex items-center gap-2 min-w-0">
                {serving.length === 0 ? (
                  <span className="text-sm text-gray-400">
                    {mode === "customer" ? "No one being served" : "Nothing in progress"}
                  </span>
                ) : serving.length === 1 ? (
                  <span className="text-sm font-bold text-green-600 truncate">
                    #{displayNumber(serving[0])} · {serving[0].name}
                  </span>
                ) : (
                  <span className="text-sm font-bold text-green-600">
                    {serving.length} {mode === "customer" ? "being served" : "in progress"}
                  </span>
                )}
                <span className="text-gray-300 text-lg leading-none">›</span>
              </span>
            </button>
            {serving.length > 0 && (
              <button
                onClick={markAllServed}
                className="flex-shrink-0 bg-green-600 text-white text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-green-700 active:scale-95 transition"
              >
                {mode === "customer" ? "✓ Mark all served" : "✓ Mark all done"}
              </button>
            )}
          </div>

          {/* Waiting list — now shown right at the top of the scrollable area */}
          <div className="flex-1 overflow-y-auto p-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
              {mode === "customer" ? `Up Next — ${waiting.length} waiting` : `Pending — ${sortedWaiting.length} task${sortedWaiting.length !== 1 ? "s" : ""}`}
            </h3>

            {waiting.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-10">
                {mode === "customer" ? "No one else in queue" : "No pending tasks"}
              </p>
            ) : (
              <ul className="space-y-2">
                {sortedWaiting.map((cust) => (
                  <li
                    key={cust.number}
                    className="bg-white rounded-lg px-4 py-3 shadow-sm flex justify-between items-center"
                  >
                    <div>
                      <span className="font-bold text-gray-800">
                        #{displayNumber(cust)}
                      </span>
                      <span className="ml-2 text-gray-600">{cust.name}</span>
                      {mode === "task" && cust.priority && (
                        <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full ${cust.priority === "high" ? "bg-red-100 text-red-600" : cust.priority === "medium" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-600"}`}>
                          {cust.priority === "high" ? "🔴 High" : cust.priority === "medium" ? "🟡 Med" : "🔵 Low"}
                        </span>
                      )}
                      {cust.notes && (
                        <p className="text-xs text-gray-400 italic mt-0.5">{cust.notes}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => serveNow(cust.number)}
                        className="bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded text-sm hover:bg-green-100 transition font-medium"
                      >
                        {mode === "customer" ? "Serve Now" : "Start Task"}
                      </button>
                      <button
                        onClick={() => requestRemoveCustomer(cust.number)}
                        className="bg-red-50 text-red-600 px-3 py-1 rounded text-sm hover:bg-red-100 transition"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={clearAll}
                className="text-sm text-red-600 border border-red-300 bg-red-50 px-4 py-2 rounded-lg hover:bg-red-100 hover:border-red-400 active:scale-95 transition font-medium"
              >
                {mode === "customer" ? "🗑 Clear entire queue & reset counter" : "🗑 Clear all tasks & reset counter"}
              </button>
              {mode === "customer" && (
                <button
                  onClick={() => setShowStats(true)}
                  className="text-sm text-indigo-600 border border-indigo-300 bg-indigo-50 px-4 py-2 rounded-lg hover:bg-indigo-100 hover:border-indigo-400 active:scale-95 transition font-medium"
                >
                  📊 Statistics
                </button>
              )}
            </div>
            <p className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-xs text-gray-400">
              <span>
                Powered by{" "}
                <a
                  href="https://customerqueue.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 font-semibold hover:underline"
                >
                  customerqueue.com
                </a>
              </span>
              <span>·</span>
              <a href="tel:+61466127889" className="text-blue-500 font-semibold hover:underline">
                📞 +61 466 127 889
              </a>
              <span>·</span>
              <a
                href="https://instagram.com/customer.queue"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-500 font-semibold hover:underline"
              >
                <img src={instaLogo} alt="Instagram" className="w-4 h-4" />
                @customer.queue
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* ── Now Serving modal ─────────────────────────────────────────────────── */}
      {showServing && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setShowServing(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
              <h3 className="text-lg font-bold text-gray-800">
                {mode === "customer" ? "Now Serving" : "In Progress"}
              </h3>
              <button
                onClick={() => setShowServing(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="px-6 pb-6 overflow-y-auto">
              {serving.length === 0 ? (
                <p className="text-gray-400 text-base py-2 text-center">{mode === "customer" ? "No customers being served" : "No tasks in progress"}</p>
              ) : serving.length === 1 ? (
                <div className="text-center">
                  <div className="text-5xl sm:text-7xl font-extrabold text-green-600 leading-none">
                    #{displayNumber(serving[0])}
                  </div>
                  <div className="text-xl font-medium text-gray-700 mt-1">{serving[0].name}</div>
                  {mode === "task" && serving[0].priority && (
                    <div className={`text-xs font-bold px-3 py-1 rounded-full mt-1 inline-block ${serving[0].priority === "high" ? "bg-red-100 text-red-600" : serving[0].priority === "medium" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-600"}`}>
                      {serving[0].priority === "high" ? "🔴 High Priority" : serving[0].priority === "medium" ? "🟡 Medium Priority" : "🔵 Low Priority"}
                    </div>
                  )}
                  {serving[0].notes && (
                    <div className="text-sm text-gray-400 mt-1 italic">{serving[0].notes}</div>
                  )}
                  <button
                    onClick={() => markServedByNumber(serving[0].number)}
                    className="mt-3 bg-green-600 text-white font-semibold py-2 px-8 rounded-lg hover:bg-green-700 active:scale-95 transition"
                  >
                    {mode === "customer" ? "✓ Mark as Served" : "✓ Mark Done"}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {serving.map((cust) => (
                    <div key={cust.number} className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3 text-center">
                      <div className="text-2xl sm:text-3xl font-extrabold text-green-600">#{displayNumber(cust)}</div>
                      <div className="text-sm font-medium text-gray-700 truncate">{cust.name}</div>
                      {cust.notes && <div className="text-xs text-gray-400 italic truncate">{cust.notes}</div>}
                      <button
                        onClick={() => markServedByNumber(cust.number)}
                        className="mt-2 w-full bg-green-600 text-white text-xs font-semibold py-1.5 rounded hover:bg-green-700 active:scale-95 transition"
                      >
                        {mode === "customer" ? "✓ Served" : "✓ Done"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Statistics modal ─────────────────────────────────────────────────── */}
      {showStats && user && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setShowStats(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">📊 Weekly Overview</h3>
              <button
                onClick={() => setShowStats(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
              <div className="bg-indigo-50 rounded-lg py-2.5 text-center">
                <p className="text-xl font-extrabold text-indigo-600 leading-none">{statsTotal}</p>
                <p className="text-[10px] font-semibold text-indigo-400 uppercase tracking-wide mt-1">Total</p>
              </div>
              <div className="bg-emerald-50 rounded-lg py-2.5 text-center">
                <p className="text-xl font-extrabold text-emerald-600 leading-none">{statsAvg}</p>
                <p className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wide mt-1">Avg/day</p>
              </div>
              <div className="bg-amber-50 rounded-lg py-2.5 text-center">
                <p className="text-xl font-extrabold text-amber-600 leading-none">{statsBest?.count ?? 0}</p>
                <p className="text-[10px] font-semibold text-amber-400 uppercase tracking-wide mt-1">Best day</p>
              </div>
              <div className="bg-rose-50 rounded-lg py-2.5 text-center">
                <p className="text-xl font-extrabold text-rose-600 leading-none">{statsDeletedTotal}</p>
                <p className="text-[10px] font-semibold text-rose-400 uppercase tracking-wide mt-1">Deleted</p>
              </div>
            </div>

            {/* Bar chart */}
            <div className="flex items-end justify-between gap-2 h-36 mb-1.5 px-1">
              {statsData.map((d) => {
                const heightPct = d.count > 0 ? Math.max((d.count / statsMax) * 100, 8) : 3;
                const isToday = d.label === "Today";
                return (
                  <div key={d.dateStr} className="flex-1 flex flex-col items-center justify-end h-full" title={`${d.label}: ${d.count}`}>
                    <span className={`text-[11px] font-bold mb-1 ${isToday ? "text-indigo-600" : "text-gray-500"}`}>
                      {d.count}
                    </span>
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${heightPct}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className={`w-full rounded-t-md ${isToday ? "bg-indigo-600" : "bg-indigo-200"}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between gap-2 px-1 mb-5">
              {statsData.map((d) => (
                <span
                  key={d.dateStr}
                  className={`flex-1 text-center text-[10px] font-semibold truncate ${
                    d.label === "Today" ? "text-indigo-600" : "text-gray-400"
                  }`}
                >
                  {d.label === "Today" ? "Today" : d.label.split(",")[0]}
                </span>
              ))}
            </div>

            <div className="pt-3 border-t border-gray-100 flex justify-between text-sm font-semibold text-gray-700">
              <span>Weekly total</span>
              <span>{statsTotal}</span>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Remove-confirmation modal ─────────────────────────────────────────── */}
      {confirmRemoveNumber !== null && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setConfirmRemoveNumber(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-4xl mb-3">🗑️</div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              {mode === "customer" ? "Remove this customer?" : "Remove this task?"}
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              {mode === "customer"
                ? "They will be removed from the queue. This cannot be undone."
                : "The task will be removed from the list. This cannot be undone."}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmRemoveNumber(null)}
                className="flex-1 bg-gray-100 text-gray-700 font-semibold py-2.5 rounded-lg hover:bg-gray-200 active:scale-95 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveCustomer}
                className="flex-1 bg-red-600 text-white font-semibold py-2.5 rounded-lg hover:bg-red-700 active:scale-95 transition"
              >
                Remove
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default QueueManager;
